import clsx from 'clsx';
import dayjs from 'dayjs';
import {MRT_ColumnDef, MRT_Row} from 'material-react-table';
import React, {useCallback, useMemo} from 'react';
import {Body} from '@lightricks/react-design-system';
import getEngagementRateString from '@/utils/getEngagementRateString';
import getFollowersCountString from '@/utils/getFollowersCountString';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {COLUMN_ID, ColumnId} from '@/types/models/creator-crm/columnId';
import {SearchDisplayCreator} from '@/types/models/creator-crm/creatorCrm';
import {TableColumnConfig} from '@/types/models/creator-crm/creatorCrmStore';
import {CreatorActionKey} from '@/views/creators/components/creator-action/CreatorAction';
import UsernameCell from '@/views/creators/components/creators-table/UsernameCell';
import getProfileImageUrl from '@/views/creators/creator-crm/utils/getProfileImageUrl';
import {
  disabledActionKeysForCreator,
  isOffPlatformCreator,
} from '@/views/creators/creator-crm/utils/offPlatformCreator';
import Checkbox from '@/components/checkbox';
import CreatorSocialsIcons from '@/components/creator-card/CreatorSocialsIcons';
import {shouldShowColumn} from '@/stores/creator-crm/utils/filterVisibilityUtils';
import CreatorActionsMenu from '../../components/creator-actions-menu';
import VettingStatusCell from '../../components/vetting-status-cell/VettingStatusCell';
import styles from './Column.module.scss';

const TRANSLATION_PREFIX = 'views.creators';

export const PAGE_SELECTION_STATE = {
  none: 'none',
  partial: 'partial',
  all: 'all',
} as const;

export type PageSelectionState =
  (typeof PAGE_SELECTION_STATE)[keyof typeof PAGE_SELECTION_STATE];

interface UseCreatorsTableColumnsProps {
  selectedPlatform: string;
  onSelectionCreatorChange: (id: string, isSelected: boolean) => void;
  onSocialNetworkClick?: (networkProvider: string, creatorId: string) => void;
  /**
   * When set, only columns with these ids are returned.
   * When undefined, all columns are returned.
   */
  enabledColumnIds?: readonly string[];
  /** Current page creators for "select all on page" header. */
  currentPageCreators?: SearchDisplayCreator[];
  /** Selection state of current-page rows for the header checkbox. */
  pageSelectionState?: PageSelectionState;
  /** Called when header "select all on page" checkbox is toggled. */
  onSelectAllPageChange?: (
    creators: SearchDisplayCreator[],
    isSelected: boolean
  ) => void;
  /** Column configs from the store – used to derive enableSorting per column. */
  tableColumns?: TableColumnConfig[];
  onCreatorAction?: (creatorId: string, actionKey: CreatorActionKey) => void;
  disabledActionKeys?: Set<CreatorActionKey>;
  /** Whether search mode is enabled - hides vetting column in search mode */
  isSearchMode?: boolean;
}

function useCreatorsTableColumns(props: UseCreatorsTableColumnsProps) {
  const {
    selectedPlatform,
    onSelectionCreatorChange,
    onSocialNetworkClick,
    enabledColumnIds,
    currentPageCreators = [],
    pageSelectionState = PAGE_SELECTION_STATE.none,
    onSelectAllPageChange,
    tableColumns: storeTableColumns,
    onCreatorAction,
    disabledActionKeys,
    isSearchMode,
  } = props;

  const isYoutube = selectedPlatform === 'youtube';

  const sortableSet = useMemo(() => {
    if (!storeTableColumns) {
      return null;
    }
    const set = new Set<string>();
    storeTableColumns.forEach((col) => {
      if (col.sortable) {
        set.add(col.id);
      }
    });
    return set;
  }, [storeTableColumns]);

  const renderUsernameCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {id, fullName, profileImage, rating} = row.original;

      return (
        <UsernameCell
          profileId={id}
          picture={getProfileImageUrl(profileImage)}
          name={fullName}
          rating={rating}
          isSelected={row.getIsSelected()}
          onSelectionChange={onSelectionCreatorChange}
          showSelectCheckbox={false}
          isOffPlatform={isOffPlatformCreator(row.original)}
        />
      );
    },
    [onSelectionCreatorChange]
  );

  const renderCreatorActions = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {id} = row.original;
      return (
        <div
          className={styles.selectCreatorCell}
          onClick={(event) => event.stopPropagation()}
          role="presentation"
        >
          <Checkbox
            checked={row.getIsSelected()}
            onChange={(checked: boolean) => {
              onSelectionCreatorChange(id, checked);
            }}
          />
          {onCreatorAction ? (
            <CreatorActionsMenu
              creatorId={id}
              onCreatorAction={onCreatorAction}
              disabledActionKeys={disabledActionKeysForCreator(
                row.original,
                disabledActionKeys
              )}
              isOffPlatform={isOffPlatformCreator(row.original)}
            />
          ) : null}
        </div>
      );
    },
    [onSelectionCreatorChange, onCreatorAction, disabledActionKeys]
  );

  const renderFollowersCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {followers} = row.original;

      return (
        <div className={styles.followersCell}>
          {getFollowersCountString(followers)}
        </div>
      );
    },
    []
  );

  const renderEngagementRateCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {engagementRate} = row.original;

      const engagementRateString = getEngagementRateString(engagementRate);

      return (
        <div
          className={clsx(
            styles.engagementRateCell,
            engagementRateString === '-' && styles.bold
          )}
        >
          {engagementRateString}
        </div>
      );
    },
    []
  );

  const renderCategoriesCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {categories} = row.original;
      if (!categories?.length) {
        return '-';
      }

      const text = categories.join(', ');

      return (
        <Body size="md" className={styles.categoriesCell}>
          {text}
        </Body>
      );
    },
    []
  );

  const renderSocialNetworksCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {socialNetworks} = row.original;

      return (
        <div className={styles.socialNetworksCell}>
          <CreatorSocialsIcons
            socialNetworks={socialNetworks}
            selectedPlatformProvider={selectedPlatform}
            onClickSocialNetworkWithId={onSocialNetworkClick}
            creatorId={row.original.id}
          />
        </div>
      );
    },
    [selectedPlatform, onSocialNetworkClick]
  );

  const renderLocationCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => (
      <span className={styles.truncatedCell}>
        {row.original.location || '-'}
      </span>
    ),
    []
  );

  const renderJoinedCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const d = row.original.signupDate;
      if (!d || !dayjs(d).isValid()) {
        return '-';
      }
      return dayjs(d).format(MONTH_SHORT_DAY_YEAR_FORMAT);
    },
    []
  );

  const renderGenderCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) => {
      const {gender} = row.original;
      return gender ?? '-';
    },
    []
  );

  const renderAgeCell = useCallback(
    ({row}: {row: MRT_Row<SearchDisplayCreator>}) =>
      row.original.age != null ? row.original.age : '-',
    []
  );

  const columns: MRT_ColumnDef<SearchDisplayCreator>[] = useMemo(
    () => [
      {
        id: COLUMN_ID.select,
        header: '',
        Header: () =>
          onSelectAllPageChange ? (
            <div
              className={styles.selectCreatorCell}
              onClick={(event) => event.stopPropagation()}
              role="presentation"
            >
              <Checkbox
                checked={pageSelectionState === PAGE_SELECTION_STATE.all}
                indeterminate={
                  pageSelectionState === PAGE_SELECTION_STATE.partial
                }
                onChange={() => {
                  onSelectAllPageChange(
                    currentPageCreators,
                    pageSelectionState !== PAGE_SELECTION_STATE.all
                  );
                }}
              />
            </div>
          ) : null,
        enableSorting: false,
        Cell: renderCreatorActions,
      } as MRT_ColumnDef<SearchDisplayCreator>,
      {
        id: COLUMN_ID.name,
        header: translate(
          `${TRANSLATION_PREFIX}.components.table.columns.name`
        ),
        accessorKey: 'username',
        enableSorting: sortableSet?.has(COLUMN_ID.name) ?? false,
        Cell: renderUsernameCell,
      },
      {
        id: COLUMN_ID.followers,
        header: isYoutube ? translate('Subscribers') : translate('Followers'),
        accessorKey: 'followers',
        enableSorting: sortableSet?.has(COLUMN_ID.followers) ?? true,
        Cell: renderFollowersCell,
      },
      {
        id: COLUMN_ID.engagementRate,
        header: isYoutube
          ? translate('Avg Eng')
          : translate(
              `${TRANSLATION_PREFIX}.components.table.columns.engagement-rate`
            ),
        accessorKey: 'engagementRate',
        enableSorting: sortableSet?.has(COLUMN_ID.engagementRate) ?? true,
        Cell: renderEngagementRateCell,
      },
      {
        id: COLUMN_ID.location,
        header: translate('Location'),
        accessorKey: 'location',
        enableSorting: sortableSet?.has(COLUMN_ID.location) ?? false,
        Cell: renderLocationCell,
      },
      {
        id: COLUMN_ID.joined,
        header: translate('Joined'),
        accessorKey: 'signupDate',
        enableSorting: sortableSet?.has(COLUMN_ID.joined) ?? false,
        Cell: renderJoinedCell,
      },
      {
        id: COLUMN_ID.categories,
        header: translate('Categories'),
        accessorKey: 'categories',
        enableSorting: sortableSet?.has(COLUMN_ID.categories) ?? false,
        Cell: renderCategoriesCell,
      },
      {
        id: COLUMN_ID.gender,
        header: translate('Gender'),
        accessorKey: 'gender',
        enableSorting: sortableSet?.has(COLUMN_ID.gender) ?? false,
        Cell: renderGenderCell,
      },
      {
        id: COLUMN_ID.age,
        header: translate('Age'),
        accessorKey: 'age',
        enableSorting: sortableSet?.has(COLUMN_ID.age) ?? false,
        Cell: renderAgeCell,
      },
      {
        id: COLUMN_ID.vettingDecision,
        header: translate('Safe Collab Status'),
        accessorKey: 'brandSafetyVetting',
        enableSorting: sortableSet?.has(COLUMN_ID.vettingDecision) ?? true,
        sortDescFirst: false,
        Cell: VettingStatusCell,
      },
      {
        id: COLUMN_ID.socialNetworks,
        header: translate('Platforms'),
        enableSorting: sortableSet?.has(COLUMN_ID.socialNetworks) ?? false,
        accessorKey: 'socialNetworks',
        Cell: renderSocialNetworksCell,
      },
    ],
    [
      isYoutube,
      sortableSet,
      currentPageCreators,
      pageSelectionState,
      onSelectAllPageChange,
      renderUsernameCell,
      renderCreatorActions,
      renderFollowersCell,
      renderEngagementRateCell,
      renderCategoriesCell,
      renderLocationCell,
      renderJoinedCell,
      renderGenderCell,
      renderAgeCell,
      renderSocialNetworksCell,
    ]
  );

  return useMemo(() => {
    let result = columns;
    // YouTube does not allow displaying data that does not come directly from their API.
    // Engagement rate is derived from our own calculation, so it cannot be shown for YouTube.
    if (isYoutube) {
      result = result.filter((column) => column.id !== 'engagementRate');
    }

    // Hide vetting column in search mode
    if (isSearchMode) {
      result = result.filter((column) =>
        shouldShowColumn(column.id as string, isSearchMode)
      );
    }

    if (!!enabledColumnIds && enabledColumnIds.length > 0) {
      const allowed = new Set(enabledColumnIds);
      result = result.filter(
        (
          column
        ): column is MRT_ColumnDef<SearchDisplayCreator> & {id: string} =>
          !!column.id && allowed.has(column.id as ColumnId)
      );
    }
    return result;
  }, [columns, isYoutube, isSearchMode, enabledColumnIds]);
}

export default useCreatorsTableColumns;
